import { useMutation } from '@apollo/client';
import { Grid } from '@mui/material';
import MultistepForm from 'app/components/MultistepForm';
import { Formik } from 'formik';

import { omit, omitBy, pick } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { validateForm } from 'utils/FormErrorHelper';
import { PermissionGeneral } from './PermissionGeneral';
import { permissionValidationSchema } from './validation';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import CREATE_PERMISSION_MUTATION from 'gql/mutations/PermissionProvider/createPermission.gql';
import UPDATE_PERMISSION_MUTATION from 'gql/mutations/PermissionProvider/updatePermission.gql';

interface Props {
  onCancel: Function;
  closeHandler: Function;
  setDisabledCancel?: Function;
  permission?: any;
}

export function PermissionModal(props: Props) {
  const [submitting, setSubmitting] = useState(false);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [createPermission] = useMutation(CREATE_PERMISSION_MUTATION);
  const [updatePermission] = useMutation(UPDATE_PERMISSION_MUTATION);

  const initialValues = () => {
    return props.permission
      ? {
          ...pick(props.permission, ['id', 'authorId']),
          recipientRoles: props.permission.recipientRoles?.map(role => role.id)
        }
      : {
          recipientRoles: []
        };
  };

  const handleSaveClick = async values => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }
    const omitFields = ['__typename', 'id'];
    const saveBody: any = { ...omit(values, omitFields) };
    Object.assign(saveBody, {
      authorId: parseInt(saveBody.authorId, 10),
      recipientRoles: saveBody.recipientRoles?.map(ent => parseInt(ent, 10))
    });

    if (values.id) {
      try {
        await updatePermission({
          variables: {
            id: values.id,
            data: omitBy(saveBody, v => v === null || v === '')
          },
          refetchQueries: ['GetPermissions']
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.managePermissionsSettings.alertsPermission.saveSuccess())
          )
        );
      } catch (e) {
        dispatch(
          AlertActions.error(
            t(translations.pages.managePermissionsSettings.alertsPermission.saveFailed())
          )
        );
      }
    } else {
      try {
        await createPermission({
          variables: {
            data: omitBy(saveBody, v => v === null || v === '')
          },
          refetchQueries: ['GetPermissions']
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.managePermissionsSettings.alertsPermission.saveSuccess())
          )
        );
      } catch (e) {
        dispatch(
          AlertActions.error(
            t(translations.pages.managePermissionsSettings.alertsPermission.saveFailed())
          )
        );
      }
    }

    props.closeHandler();
    setSubmitting(false);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(false);
    }
  };

  const steps = ['Permission Creation'];

  const PermissionModalStep = ({ step }) => {
    switch (step) {
      case 0:
        return <PermissionGeneral />;
      default:
        return null;
    }
  };

  return (
    <Formik
      isInitialValid={props.permission ? true : false}
      initialValues={initialValues()}
      onSubmit={values => {
        handleSaveClick(values);
      }}
      validationSchema={permissionValidationSchema()}
      validate={values => {
        return validateForm(values, permissionValidationSchema(), dispatch);
      }}
    >
      <MultistepForm submitting={submitting} steps={steps} onCancel={props.onCancel} hideStepper>
        {activeStep => (
          <Grid container className="permission-detail-modal">
            <PermissionModalStep step={activeStep} />
          </Grid>
        )}
      </MultistepForm>
    </Formik>
  );
}
